import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { io } from 'socket.io-client';

import { RootState } from '../../../store/store';
import {
  CompradoresCompras,
  EvolucaoMensalCompras,
  FiltrosCompras,
  FornecedoresCompras,
  IdentificacaoDeOficina,
  OficinasCompras,
  ResumoCompras,
  StatusCompras,
  UltimasRequisicoes,
  ItensMaisRequisitados,
  RequisicoesPorPlaca,
  CentroDeCusto,
  RequisicoesPorCentroCusto,
  RequisicoesPorSolicitante,
  FilaPorComprador,
  fetchCompradoresCompras,
  fetchEvolucaoMensalCompras,
  fetchFilaPorComprador,
  fetchFornecedoresCompras,
  fetchItensMaisRequisitados,
  fetchOficinasCompras,
  fetchResumoCompras,
  fetchStatusCompras,
  fetchUltimasRequisicoes,
  fetchRequisicoesPorPlaca,
  fetchCentrosDeCusto,
  fetchRequisicoesPorCentroCusto,
  fetchRequisicoesPorSolicitante,
} from '../../../api/comprasDashboardApi';
import { blocosDoModo } from '../blocosDoModo';

/**
 * A carga do Dashboard de Compras, e o que faz a tela andar sozinha (at-100).
 *
 * ## Erro é POR BLOCO, nunca da tela inteira
 *
 * Cada consulta guarda o próprio erro. Um bloco que falha vira faixa; os outros continuam
 * mostrando o que carregaram. 🔴 E bloco com erro **não renderiza zero**: "0 requisições
 * pendentes" é afirmação de que Compras está em dia, e ninguém deve chegar a ela por causa de uma
 * rede instável (regra da at-60).
 *
 * ## O tempo real possível
 *
 * O backend emite `compras:atualizado` quando uma varredura encontra diferença — em até 3 min da
 * mudança no ERP. O evento **não carrega dado**: ele só diz que vale perguntar de novo. O polling
 * de 5 minutos fica como rede de segurança para o caso de o socket cair; socket é aceleração,
 * nunca dependência.
 */

const INTERVALO_DE_SEGURANCA_MS = 5 * 60 * 1000;

export interface BlocoCarregado<T> {
  dados: T | null;
  carregando: boolean;
  erro: boolean;
}

function estadoInicial<T>(): BlocoCarregado<T> {
  return { dados: null, carregando: true, erro: false };
}

/**
 * O estado de um bloco que o modo atual **não monta** (at-111).
 *
 * ⚠️ Não é `estadoInicial`: aquele diz "carregando", e um bloco não montado nunca sai desse
 * estado. Se alguém o renderizasse por engano, veria um esqueleto eterno em vez de perceber o
 * erro de composição.
 */
function naoMontado<T>(): BlocoCarregado<T> {
  return { dados: null, carregando: false, erro: false };
}

/**
 * Os filtros que valem para UM card, e não para o painel (at-110).
 *
 * 🔴 Separados de `FiltrosCompras` de propósito: mudar um deles refaz **um** bloco, não a tela. Se
 * entrassem no filtro geral, escolher "Em cotação" na carga por comprador mudaria também o funil, a
 * evolução e os fornecedores — números que ninguém pediu para filtrar.
 */
export interface FiltrosDeCard {
  /** Situação do ERP no card "Carga por comprador". Vazio = todas. */
  compradores?: string[];
  /** Situações do ERP no card "Requisições por placa". Vazio = todas. */
  placas?: string[];
  /**
   * Recorte do card "Requisições por oficina" (at-117). Ausente = todas.
   *
   * ⚠️ Um valor só, ao contrário dos dois acima: as opções são mutuamente exclusivas, e por isso
   * o controle é um `select` e não o modal de caixas de seleção.
   */
  oficinas?: IdentificacaoDeOficina;
  /** Trecho de placa digitado no card "Requisições por placa" (at-118). Vazio = todas. */
  buscaPlaca?: string;
  /** Página do card "Requisições por placa" (at-118). Ausente = 1. */
  paginaPlacas?: number;
}

/**
 * Linhas por página do card "Requisições por placa" (at-118, `BUG-014`).
 *
 * 🔴 Vai ao servidor como `limite`: a busca e a paginação do card são do SERVIDOR, sobre todas as
 * placas do período. Até a at-118 o card pedia a rota sem limite, recebia só as 25 primeiras e
 * buscava localmente nelas — placa fora do topo 25 não era achada (a OYF4G25 era a 45ª).
 */
export const POR_PAGINA_PLACAS = 10;

const consultaDePlacas = (f: FiltrosCompras, c: FiltrosDeCard) =>
  fetchRequisicoesPorPlaca({
    ...f,
    situacoes: c.placas,
    busca: c.buscaPlaca || undefined,
    pagina: c.paginaPlacas ?? 1,
    limite: POR_PAGINA_PLACAS,
  });

export function useComprasDashboard(filtros: FiltrosCompras, filtrosDeCard: FiltrosDeCard = {}) {
  const [resumo, setResumo] = useState<BlocoCarregado<ResumoCompras>>(estadoInicial);
  const [evolucao, setEvolucao] = useState<BlocoCarregado<EvolucaoMensalCompras>>(estadoInicial);
  const [compradores, setCompradores] = useState<BlocoCarregado<CompradoresCompras>>(estadoInicial);
  // at-119: o card de barras ao lado da carga — a fila de cada comprador, hoje. Fora de
  // `blocosDoModo` de propósito: é sobre a pessoa, não sobre peça, e vale em qualquer centro.
  const [filaPorComprador, setFilaPorComprador] =
    useState<BlocoCarregado<FilaPorComprador>>(estadoInicial);
  const [fornecedores, setFornecedores] = useState<BlocoCarregado<FornecedoresCompras>>(estadoInicial);
  const [oficinas, setOficinas] = useState<BlocoCarregado<OficinasCompras>>(estadoInicial);
  const [requisicoes, setRequisicoes] = useState<BlocoCarregado<UltimasRequisicoes>>(estadoInicial);
  const [status, setStatus] = useState<BlocoCarregado<StatusCompras>>(estadoInicial);
  // at-109: o agrupamento por veiculo pedido pela area de Compras.
  const [placas, setPlacas] = useState<BlocoCarregado<RequisicoesPorPlaca>>(estadoInicial);
  // at-111, reancorado no centro de custo na at-112: `porCentroCusto` existe sempre; os outros
  // dois, só onde o recorte os monta — ver `blocosDoModo`, a regra única lida também pela página.
  const [porCentroCusto, setPorCentroCusto] =
    useState<BlocoCarregado<RequisicoesPorCentroCusto>>(estadoInicial);
  /*
   * O cadastro de centros de custo (at-112).
   *
   * 🔴 O hook precisa dele para saber **quais blocos montar**: é o `grupo` de cada centro que diz
   * se o recorte é da oficina (e portanto se oficina/placa/entrega respondem alguma coisa). Sem
   * ele, `blocosDoModo` cai no modo misto, que mostra mais e não menos — errar para o lado de
   * esconder deixaria a tela pobre justamente enquanto ela carrega.
   */
  const [centros, setCentros] = useState<CentroDeCusto[] | null>(null);
  const [solicitantes, setSolicitantes] =
    useState<BlocoCarregado<RequisicoesPorSolicitante>>(estadoInicial);
  const [itens, setItens] = useState<BlocoCarregado<ItensMaisRequisitados>>(estadoInicial);
  /*
   * ⚠️ **Não há estado de tempo de atendimento aqui, e isso é deliberado** (at-116).
   *
   * O bloco `TempoPorCompradorBloco` não desenha dado nenhum enquanto a Nasajon não expuser o
   * histórico de mudanças de situação — bloco que não mostra número não paga carga, que é a mesma
   * decisão que `entrega-no-prazo` teve na at-112. As rotas continuam no backend, servindo, sem
   * consumidor.
   */

  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  // Os filtros entram num ref para o socket não reassinar a cada digitação do modal.
  const filtrosRef = useRef(filtros);
  filtrosRef.current = filtros;
  // Idem para os filtros de card: eles participam da carga geral, mas não podem fazer o socket
  // reassinar nem a função de carga mudar de identidade a cada escolha no select.
  const cardRef = useRef(filtrosDeCard);
  cardRef.current = filtrosDeCard;
  const centrosRef = useRef<CentroDeCusto[] | null>(null);
  centrosRef.current = centros;

  /**
   * 🔴 Um `try/catch` por bloco, sempre. Erro em um não pode apagar o que os outros carregaram, e
   * bloco com erro guarda `dados: null` — nunca zero.
   */
  const carregarBloco = useCallback(
    async (buscar: () => Promise<unknown>, definir: (e: BlocoCarregado<never>) => void) => {
      try {
        const dados = await buscar();
        definir({ dados, carregando: false, erro: false } as never);
      } catch {
        definir({ dados: null, carregando: false, erro: true } as never);
      }
    },
    [],
  );

  /**
   * A carga da tela inteira.
   *
   * 🔴 `anunciarCarga` separa os dois motivos de recarregar, e a distinção é a razão de ele
   * existir (at-115): **mudança de filtro** precisa dizer que está buscando — sem isso a tela
   * segue exibindo os números do filtro ANTERIOR, sem nenhum sinal, e quem aplicou o filtro lê
   * aqueles números como resposta. **Atualização de fundo** (o socket e o polling de 5 min) não
   * pode piscar: ela acontece sozinha, o usuário não pediu nada, e transformar a tela em
   * esqueleto a cada varredura do espelho tornaria o painel inutilizável.
   */
  const carregar = useCallback(async ({ anunciarCarga = false } = {}) => {
    const f = filtrosRef.current;
    const c = cardRef.current;

    if (anunciarCarga) {
      // ⚠️ `dados` é PRESERVADO (só `carregando` vira true): quem tem esqueleto é quem nunca
      // carregou nada; os demais escurecem sobre o número antigo até o novo chegar. Zerar `dados`
      // aqui faria a tela inteira sumir e voltar a cada ajuste de filtro.
      const marcar = <T,>(atual: BlocoCarregado<T>): BlocoCarregado<T> =>
        atual.carregando ? atual : { ...atual, carregando: true, erro: false };
      setResumo(marcar);
      setEvolucao(marcar);
      setCompradores(marcar);
      setFilaPorComprador(marcar);
      setFornecedores(marcar);
      setOficinas(marcar);
      setRequisicoes(marcar);
      setPlacas(marcar);
      setPorCentroCusto(marcar);
      setSolicitantes(marcar);
      setItens(marcar);
    }
    /*
     * 🔴 at-111 — o que NÃO é montado também não é buscado.
     *
     * Fora da oficina o painel não mostra oficina nem placa: buscá-las assim mesmo gastaria
     * requisição por carga para desenhar nada. E o inverso é o que o teste mede — a tela não pode
     * chamar a rota de um card que ela esconde.
     *
     * ⚠️ `entrega-no-prazo` não está nesta lista: desde a at-125 ela é carregada pela própria
     * aba Pedidos (`EntregaNoPrazoDaAba`, em `BlocosPedidos.tsx`), montada só no recorte de
     * oficina — então a regra "o que não é montado não é buscado" vale para ela por construção.
     */
    const blocos = blocosDoModo(f.centrosCusto, centrosRef.current ?? undefined);
    const pares: Array<[() => Promise<unknown>, (e: BlocoCarregado<never>) => void]> = [
      [() => fetchResumoCompras(f), setResumo as never],
      [() => fetchEvolucaoMensalCompras(f), setEvolucao as never],
      [() => fetchCompradoresCompras({ ...f, situacoes: c.compradores }), setCompradores as never],
      [() => fetchFilaPorComprador(f), setFilaPorComprador as never],
      [() => fetchFornecedoresCompras(f), setFornecedores as never],
      [() => fetchUltimasRequisicoes({ ...f, limite: 15 }), setRequisicoes as never],
      [() => fetchStatusCompras(), setStatus as never],
      [() => fetchRequisicoesPorCentroCusto(f), setPorCentroCusto as never],
    ];

    if (blocos.cardsDePeca) {
      pares.push([() => fetchOficinasCompras({ ...f, identificacao: c.oficinas }), setOficinas as never]);
      pares.push([
        () => consultaDePlacas(f, c),
        setPlacas as never,
      ]);
    } else {
      setOficinas(naoMontado());
      setPlacas(naoMontado());
    }

    if (blocos.solicitantes) {
      pares.push([() => fetchRequisicoesPorSolicitante(f), setSolicitantes as never]);
    } else {
      setSolicitantes(naoMontado());
    }

    if (blocos.itens) {
      pares.push([() => fetchItensMaisRequisitados(f), setItens as never]);
    } else {
      setItens(naoMontado());
    }

    await Promise.all(pares.map(([buscar, definir]) => carregarBloco(buscar, definir)));
  }, [carregarBloco]);

  /*
   * O cadastro dos centros, uma vez por montagem (at-112).
   *
   * ⚠️ Falha aqui **não** vira lista vazia nem derruba a tela: sem o cadastro o painel segue no
   * modo misto e mostra tudo. O aviso de que o filtro não pôde ser aplicado vem do backend, em
   * `centroCustoNaoAplicado` — é lá que a informação é confiável.
   */
  useEffect(() => {
    void (async () => {
      try {
        setCentros((await fetchCentrosDeCusto()).centros);
      } catch {
        setCentros(null);
      }
    })();
  }, []);

  /*
   * ⚠️ A lista de centros entra nas dependências como TEXTO (`join`), não como array: um array
   * novo a cada render faria o efeito disparar em toda renderização, e a tela recarregaria sozinha
   * sem ninguém ter mexido em filtro nenhum.
   *
   * 🔴 A segunda dependência é o RESULTADO de `blocosDoModo`, não o cadastro de centros.
   *
   * O cadastro chega de forma assíncrona, depois da primeira carga. Pô-lo nas dependências fazia
   * a tela INTEIRA recarregar na abertura — duas vezes cada rota, sem ninguém ter mexido em
   * filtro nenhum (pego pelo sentinela "escolher um status refaz só o card"). O que precisa
   * disparar carga nova é a mudança dos BLOCOS: ela só acontece quando há seleção, porque sem
   * ela o modo é misto com ou sem cadastro.
   */
  const chaveDosCentros = (filtros.centrosCusto ?? []).join(',');
  const blocosAgora = blocosDoModo(filtros.centrosCusto, centros ?? undefined);
  const chaveDosBlocos = `${blocosAgora.cardsDePeca}|${blocosAgora.solicitantes}|${blocosAgora.itens}`;
  useEffect(() => {
    // 🔴 `anunciarCarga`: este efeito é o da MUDANÇA DE FILTRO (e a carga inicial). O socket e o
    // polling chamam `carregar()` sem o sinalizador — ver `carregar`.
    void carregar({ anunciarCarga: true });
  }, [carregar, chaveDosCentros, chaveDosBlocos, filtros.de, filtros.ate, filtros.comprador]);

  /*
   * at-110 — os dois filtros de card refazem SÓ o próprio bloco.
   *
   * ⚠️ `primeiraCarga` existe porque a carga geral acima já traz os dois com o filtro em vigor: sem
   * a guarda, abrir a tela dispararia duas consultas de cada um. É o mesmo motivo pelo qual os
   * filtros entram na carga geral em vez de viverem só aqui — trocar o período não pode devolver os
   * cards ao estado "todos os status" sem o usuário ter mexido no select.
   */
  const primeiraCarga = useRef(true);
  useEffect(() => {
    if (primeiraCarga.current) return;
    setCompradores((atual) => ({ ...atual, carregando: true }));
    void carregarBloco(
      () => fetchCompradoresCompras({ ...filtrosRef.current, situacoes: filtrosDeCard.compradores }),
      setCompradores as never,
    );
    // ⚠️ A dependência é a lista em TEXTO: array novo a cada render refaria a carga sempre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregarBloco, (filtrosDeCard.compradores ?? []).join(',')]);

  useEffect(() => {
    if (primeiraCarga.current) return;
    // ⚠️ at-111: quando o recorte não é da oficina, o card de placas não é montado — o filtro que
    // dispara este efeito nem existe ali, e buscar mesmo assim reintroduziria a consulta que o
    // modo dispensa.
    if (!blocosDoModo(filtrosRef.current.centrosCusto, centrosRef.current ?? undefined).cardsDePeca)
      return;
    setPlacas((atual) => ({ ...atual, carregando: true }));
    void carregarBloco(
      () => consultaDePlacas(filtrosRef.current, cardRef.current),
      setPlacas as never,
    );
    // ⚠️ Numa linha só: o `eslint-disable-next-line` só alcança a linha seguinte, e com o array
    // quebrado o `CI=true npm run build` reprova pela expressão complexa na linha de baixo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregarBloco, (filtrosDeCard.placas ?? []).join(','), filtrosDeCard.buscaPlaca, filtrosDeCard.paginaPlacas]);

  // at-117 — o filtro de identificação do card de oficinas, no mesmo molde dos dois acima.
  useEffect(() => {
    if (primeiraCarga.current) return;
    // Mesma guarda do card de placas: fora do recorte de oficina este card não é montado.
    if (!blocosDoModo(filtrosRef.current.centrosCusto, centrosRef.current ?? undefined).cardsDePeca)
      return;
    setOficinas((atual) => ({ ...atual, carregando: true }));
    void carregarBloco(
      () => fetchOficinasCompras({ ...filtrosRef.current, identificacao: filtrosDeCard.oficinas }),
      setOficinas as never,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregarBloco, filtrosDeCard.oficinas]);

  useEffect(() => {
    primeiraCarga.current = false;
  }, []);

  // Rede de segurança: se o socket cair, a tela ainda envelhece no máximo 5 minutos.
  useEffect(() => {
    const timer = setInterval(() => void carregar(), INTERVALO_DE_SEGURANCA_MS);
    return () => clearInterval(timer);
  }, [carregar]);

  useEffect(() => {
    if (!accessToken) return undefined;

    const socket = io(process.env.REACT_APP_BACKEND_URL ?? '', {
      transports: ['websocket'],
      auth: { token: accessToken },
    });

    // Payload vazio por contrato: o evento só diz "vale perguntar de novo". Aplicar dado vindo do
    // socket criaria uma segunda fonte de verdade, que divergiria da rota no primeiro ajuste.
    const aoAtualizar = () => void carregar();
    socket.on('compras:atualizado', aoAtualizar);

    return () => {
      socket.off('compras:atualizado', aoAtualizar);
      socket.disconnect();
    };
  }, [accessToken, carregar]);

  return {
    resumo,
    evolucao,
    compradores,
    filaPorComprador,
    fornecedores,
    oficinas,
    requisicoes,
    placas,
    porCentroCusto,
    centros,
    solicitantes,
    itens,
    status,
    recarregar: carregar,
  };
}
